import { formatNumericDate } from './dates';
import { addressKey } from './geography';
import {
  CHURCH_STATUS_LABEL,
  CIVIL_STATUS_LABEL,
  JOINED_VIA_LABEL,
  addressLine,
  formatCep,
  formatPhone,
  memberEmailError,
  memberNameError,
  memberPhoneError,
  memberProfileErrors,
  normalizeEmail,
  phoneDigits,
  showsOriginChurch,
} from './members';
import { MATCH_LIKELY, MATCH_POSSIBLE, nameSimilarity } from './nameMatch';
import { normalizeRegistration } from './registrations';
import type { Member, MergeField, Registration, RegistrationData } from './types';

export type MatchEvidenceField = 'name' | 'email' | 'phone' | 'birthDate' | 'sex' | 'address';
export type MatchSignal = 'match' | 'partial' | 'conflict';
export type MatchProbability = 'high' | 'medium' | 'low';

export interface MatchEvidence {
  field: MatchEvidenceField;
  signal: MatchSignal;
  label: string;
}

export interface RegistrationMatch {
  memberId: string;
  probability: number;
  level: MatchProbability;
  evidence: MatchEvidence[];
  blocking: ('name' | 'email')[];
}

export const MATCH_PROBABILITY_LABEL: Record<MatchProbability, string> = {
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

const PRIOR = -3;
const HIGH_FROM = 80;
const MEDIUM_FROM = 50;
const VISIBLE_FROM = 15;
const MAX_REGISTRATION_MATCHES = 3;

function scoreOf(
  data: RegistrationData,
  member: Member
): { score: number; evidence: MatchEvidence[]; contact: boolean; similar: boolean; sameName: boolean } {
  const evidence: MatchEvidence[] = [];
  let score = PRIOR;
  const add = (field: MatchEvidenceField, signal: MatchSignal, label: string, weight: number) => {
    score += weight;
    if (label) evidence.push({ field, signal, label });
  };

  const similarity = nameSimilarity(data.name, member.name);

  if (similarity === 1) add('name', 'match', 'Mesmo nome', 4);
  else if (similarity >= MATCH_LIKELY) add('name', 'partial', 'Nome muito parecido', 2.5);
  else if (similarity >= MATCH_POSSIBLE) add('name', 'partial', 'Nome parecido', 1);
  else add('name', 'conflict', 'Nome diferente', -2.5);

  const email = normalizeEmail(data.email);
  const memberEmail = normalizeEmail(member.email);
  const sameEmail = !!email && email === memberEmail;

  if (sameEmail) add('email', 'match', 'Mesmo e-mail', 4);
  else if (email && memberEmail) add('email', 'conflict', 'E-mail diferente', -0.5);

  const phone = phoneDigits(data.phone);
  const memberPhone = phoneDigits(member.phone);
  const samePhone = !!phone && phone === memberPhone;

  if (samePhone) add('phone', 'match', 'Mesmo telefone', 3.5);
  else if (phone && memberPhone) add('phone', 'conflict', 'Telefone diferente', -0.5);

  if (data.birthDate && member.birthDate) {
    if (data.birthDate === member.birthDate) add('birthDate', 'match', 'Mesma data de nascimento', 3);
    else add('birthDate', 'conflict', 'Data de nascimento diferente', -3);
  }

  if (data.sex && data.sex !== member.sex) add('sex', 'conflict', 'Sexo diferente', -3);

  const key = addressKey(data.address);
  const memberKey = addressKey(member.address);

  if (key && key === memberKey) add('address', 'match', 'Mesmo endereço', 1.5);
  else if (data.address.cep && data.address.cep === member.address.cep)
    add('address', 'partial', 'Mesmo CEP', 0.5);
  else if (key && memberKey) add('address', 'conflict', 'Endereço diferente', -0.3);

  return {
    score,
    evidence,
    contact: sameEmail || samePhone,
    similar: similarity >= MATCH_POSSIBLE,
    sameName: memberNameError([member], '', data.name) !== null,
  };
}

function levelOf(probability: number): MatchProbability {
  if (probability >= HIGH_FROM) return 'high';

  return probability >= MEDIUM_FROM ? 'medium' : 'low';
}

export function registrationMatches(data: RegistrationData, members: readonly Member[]): RegistrationMatch[] {
  const clean = normalizeRegistration(data);
  const sorted = members
    .flatMap((member): RegistrationMatch[] => {
      const { score, evidence, contact, similar, sameName } = scoreOf(clean, member);
      const probability = Math.max(1, Math.min(99, Math.round(100 / (1 + Math.exp(-score)))));

      if (!contact && !similar && !sameName && probability < VISIBLE_FROM) return [];

      const blocking: RegistrationMatch['blocking'] = [];

      if (sameName) blocking.push('name');
      if (clean.email && normalizeEmail(member.email) === clean.email) blocking.push('email');

      return [{ memberId: member.id, probability, level: levelOf(probability), evidence, blocking }];
    })
    .sort((a, b) => b.probability - a.probability);

  return sorted.filter((match, i) => i < MAX_REGISTRATION_MATCHES || match.blocking.length > 0);
}

export const MERGE_SECTIONS: readonly { title: string; fields: readonly MergeField[] }[] = [
  { title: 'Identificação', fields: ['photo', 'name', 'birthDate', 'sex', 'civilStatus', 'profession'] },
  { title: 'Contato', fields: ['phone', 'email'] },
  { title: 'Endereço', fields: ['address'] },
  {
    title: 'Vida na igreja',
    fields: ['churchStatus', 'memberSince', 'joinedVia', 'baptismDate', 'originChurch'],
  },
];

export const MERGE_FIELDS: readonly MergeField[] = MERGE_SECTIONS.flatMap((section) => section.fields);

export const MERGE_FIELD_LABEL: Record<MergeField, string> = {
  photo: 'Foto',
  name: 'Nome',
  birthDate: 'Nascimento',
  sex: 'Sexo',
  civilStatus: 'Estado civil',
  profession: 'Profissão',
  phone: 'Telefone',
  email: 'E-mail',
  address: 'Endereço',
  churchStatus: 'Situação na igreja',
  memberSince: 'Membro desde',
  joinedVia: 'Forma de ingresso',
  baptismDate: 'Batismo',
  originChurch: 'Igreja de origem',
};

export const MERGE_FIELD_WARNING: Partial<Record<MergeField, string>> = {
  email:
    'Se a pessoa já tem acesso, o login passa a ser este e-mail. Confirme com ela antes: o dado veio de um formulário público.',
  sex: 'Pode gerar conflito nas funções e escalas com restrição de sexo.',
};

export type MergeRowState = 'same' | 'different' | 'onlyCurrent' | 'onlyIncoming';

export interface MergeRow {
  field: MergeField;
  state: MergeRowState;
  current: string;
  incoming: string;
}

/**
 * O que se compara: os dados do formulário e a foto (o endereço — dois endereços nunca são
 * iguais, então a foto enviada é sempre uma escolha, e a tela mostra as imagens).
 */
type MergeValues = Pick<RegistrationData, Exclude<MergeField, 'photo'>> & { photo: string | null };

/** Os valores da solicitação como entram no cadastro. */
function incomingValues(registration: Registration): MergeValues {
  return { ...normalizeRegistration(registration.data), photo: registration.photo };
}

const dateText = (date: string | null) => (date ? formatNumericDate(date) : '');

function fieldText(field: MergeField, values: MergeValues): string {
  switch (field) {
    case 'birthDate':
    case 'memberSince':
    case 'baptismDate':
      return dateText(values[field]);
    case 'sex':
      if (!values.sex) return '';
      return values.sex === 'F' ? 'Feminino' : 'Masculino';
    case 'civilStatus':
      return values.civilStatus ? CIVIL_STATUS_LABEL[values.civilStatus] : '';
    case 'churchStatus':
      return values.churchStatus ? CHURCH_STATUS_LABEL[values.churchStatus] : '';
    case 'joinedVia':
      return values.joinedVia ? JOINED_VIA_LABEL[values.joinedVia] : '';
    case 'phone':
      return values.phone ? formatPhone(values.phone) : '';
    case 'photo':
      return values.photo ?? '';
    case 'address':
      return [addressLine(values.address), values.address.cep ? `CEP ${formatCep(values.address.cep)}` : '']
        .filter(Boolean)
        .join(' · ');
    default:
      return values[field].trim();
  }
}

function rowState(current: string, incoming: string): MergeRowState | null {
  if (!current && !incoming) return null;
  if (current === incoming) return 'same';
  if (!incoming) return 'onlyCurrent';

  return current ? 'different' : 'onlyIncoming';
}

export function mergeRows(member: Member, registration: Registration): MergeRow[] {
  const incoming = incomingValues(registration);

  return MERGE_FIELDS.flatMap((field): MergeRow[] => {
    const currentText = fieldText(field, member);
    const incomingText = fieldText(field, incoming);
    const state = rowState(currentText, incomingText);

    return state ? [{ field, state, current: currentText, incoming: incomingText }] : [];
  });
}

export function defaultMergeFields(rows: readonly MergeRow[]): MergeField[] {
  return rows.filter((row) => row.state === 'onlyIncoming').map((row) => row.field);
}

export function allIncomingMergeFields(rows: readonly MergeRow[]): MergeField[] {
  return rows
    .filter(
      (row) => row.state === 'onlyIncoming' || (row.state === 'different' && !MERGE_FIELD_WARNING[row.field])
    )
    .map((row) => row.field);
}

export function applyMerge(
  member: Member,
  registration: Registration,
  fields: readonly MergeField[]
): Member {
  const incoming = incomingValues(registration);
  const merged: Member = { ...member };

  for (const field of fields) Object.assign(merged, { [field]: incoming[field] });

  return showsOriginChurch(merged.joinedVia) ? merged : { ...merged, originChurch: '' };
}

export function mergeError(
  registration: Registration,
  memberId: string,
  fields: readonly MergeField[],
  members: readonly Member[],
  today: string
): string | null {
  if (registration.status !== 'pending') return 'Esta solicitação já foi analisada.';

  const member = members.find((m) => m.id === memberId);

  if (!member) return 'Cadastro não encontrado.';

  const rows = new Map(mergeRows(member, registration).map((row) => [row.field, row.state]));

  for (const field of new Set(fields)) {
    const state = rows.get(field);

    if (state !== 'different' && state !== 'onlyIncoming')
      return `A solicitação não traz um valor novo para ${MERGE_FIELD_LABEL[field].toLowerCase()}.`;
  }

  const merged = applyMerge(member, registration, fields);
  const [profile] = Object.values(memberProfileErrors(merged, today));

  return (
    memberNameError(members, member.id, merged.name) ??
    memberEmailError(members, member.id, merged.email) ??
    memberPhoneError(merged.phone) ??
    profile ??
    null
  );
}
